export interface TutorialSafeAreaInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface TutorialSkipButtonFrame {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface TutorialSkipButtonMetrics {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly labelWidth: number;
  readonly safeAreaInsets?: TutorialSafeAreaInsets;
  readonly messagePanel?: TutorialSkipButtonAnchorRect | null;
}

export interface TutorialSkipButtonAnchorRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

const BUTTON = {
  marginX: 16,
  marginY: 18,
  messageGap: 8,
  minWidth: 58,
  height: 30,
  paddingX: 18,
} as const;

const ZERO_INSETS: TutorialSafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };

export function computeTutorialSkipButtonFrame(
  metrics: TutorialSkipButtonMetrics,
): TutorialSkipButtonFrame {
  const safe = metrics.safeAreaInsets ?? ZERO_INSETS;
  const width = Math.max(BUTTON.minWidth, metrics.labelWidth + BUTTON.paddingX * 2);
  const safeLeft = safe.left + BUTTON.marginX;
  const safeRight = metrics.screenWidth - safe.right - BUTTON.marginX;
  const safeTop = safe.top + BUTTON.marginY;
  const safeBottom = metrics.screenHeight - safe.bottom - BUTTON.marginY;
  if (metrics.messagePanel) {
    const panel = metrics.messagePanel;
    const x = clamp(panel.x + panel.width - width / 2, safeLeft + width / 2, safeRight - width / 2);
    const aboveY = panel.y - BUTTON.messageGap - BUTTON.height / 2;
    const belowY = panel.y + panel.height + BUTTON.messageGap + BUTTON.height / 2;
    const minY = safeTop + BUTTON.height / 2;
    const maxY = safeBottom - BUTTON.height / 2;
    const y = aboveY >= minY ? aboveY : belowY <= maxY ? belowY : clamp(aboveY, minY, maxY);
    return { x, y, width, height: BUTTON.height };
  }
  return {
    x: safeRight - width / 2,
    y: safeTop + BUTTON.height / 2,
    width,
    height: BUTTON.height,
  };
}

function clamp(value: number, min: number, max: number): number {
  if (min > max) return (min + max) / 2;
  return Math.min(max, Math.max(min, value));
}
